import React, { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import type {
  ResolvedBoundaryStudyPolygon,
  SnapGuide,
  SquareCornersPreview,
} from '../../lib/boundaryAlignmentTools';

/** Normalize only display winding; canonical ring order and hashes remain untouched. */
export function polygonDisplayPaths(
  outer: Array<{ lat: number; lng: number }>,
  holes: Array<Array<{ lat: number; lng: number }>> = [],
): Array<Array<{ lat: number; lng: number }>> {
  const signedArea = (ring: Array<{ lat: number; lng: number }>) => ring.reduce((sum, p, i) => {
    const q = ring[(i + 1) % ring.length]!;
    return sum + p.lng * q.lat - q.lng * p.lat;
  }, 0);
  const outerSign = Math.sign(signedArea(outer));
  return [outer, ...holes.map(hole => Math.sign(signedArea(hole)) === outerSign ? [...hole].reverse() : hole)];
}

export interface StudyAreaSelectedOverlaysProps {
  polygons: readonly ResolvedBoundaryStudyPolygon[];
  /** Optional stroke color override (defaults to each polygon's `#2563eb` blue). */
  strokeColor?: string;
  fillOpacity?: number;
}

/**
 * Renders study-area-selected layers (`selectedForStudyArea === true`) in blue (`#2563eb`)
 * inside the Boundary / Multi-Parcel drawers regardless of `layer.visible`, so users can
 * trace, snap, align, or compare against selected study-area geometry even when general
 * map-layer display is toggled off (#244 #issuecomment-6607632578).
 */
export const StudyAreaSelectedOverlays: React.FC<StudyAreaSelectedOverlaysProps> = ({
  polygons,
  strokeColor,
  fillOpacity,
}) => {
  const map = useMap();
  const overlaysRef = useRef<google.maps.Polygon[]>([]);

  useEffect(() => {
    overlaysRef.current.forEach((p) => p.setMap(null));
    overlaysRef.current = [];
    if (!map || typeof google === 'undefined' || !google.maps || !google.maps.Polygon) return;

    const created: google.maps.Polygon[] = [];
    for (const poly of polygons) {
      if (!Array.isArray(poly.points) || poly.points.length < 3) continue;
      const color = strokeColor ?? poly.strokeColor ?? '#2563eb';
      const alpha = fillOpacity ?? poly.fillOpacity ?? 0.18;
      const gPoly = new google.maps.Polygon({
        paths: polygonDisplayPaths(poly.points.map(([lat, lng]) => ({ lat, lng })), poly.holes),
        strokeColor: color,
        strokeOpacity: 0.95,
        strokeWeight: 2.5,
        fillColor: color,
        fillOpacity: alpha,
        clickable: false,
        zIndex: 25,
        map,
      });
      created.push(gPoly);
    }
    overlaysRef.current = created;

    return () => {
      created.forEach((p) => p.setMap(null));
      overlaysRef.current = [];
    };
  }, [map, polygons, strokeColor, fillOpacity]);

  if (polygons.length === 0) return null;
  return (
    <div
      data-testid="study-area-blue-overlays"
      data-polygon-count={polygons.length}
      className="sr-only"
    >
      {polygons.map((p) => (
        <span
          key={p.id}
          data-testid={`study-area-blue-overlay-${p.id}`}
          data-stroke-color={strokeColor ?? p.strokeColor}
        >
          {p.name}
        </span>
      ))}
    </div>
  );
};

export interface BoundaryAlignmentPreviewOverlayProps {
  preview?: SquareCornersPreview | null;
  guides?: readonly SnapGuide[];
}

/**
 * Renders opt-in alignment/snap guide lines and Square/Rectangle preview polygon
 * before the user clicks Apply or Cancel (#244 #issuecomment-6607686195, #issuecomment-6607716290).
 */
export const BoundaryAlignmentPreviewOverlay: React.FC<BoundaryAlignmentPreviewOverlayProps> = ({
  preview,
  guides = [],
}) => {
  const map = useMap();
  const polyRef = useRef<google.maps.Polygon | null>(null);
  const guideRefs = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (polyRef.current) {
      polyRef.current.setMap(null);
      polyRef.current = null;
    }
    if (
      map &&
      preview &&
      preview.squaredPoints.length >= 3 &&
      typeof google !== 'undefined' &&
      google.maps &&
      google.maps.Polygon
    ) {
      polyRef.current = new google.maps.Polygon({
        paths: preview.squaredPoints.map(([lat, lng]) => ({ lat, lng })),
        strokeColor: '#0ea5e9',
        strokeOpacity: 0.95,
        strokeWeight: 2.5,
        fillColor: '#0ea5e9',
        fillOpacity: 0.14,
        clickable: false,
        zIndex: 60,
        map,
      });
    }
    return () => {
      if (polyRef.current) {
        polyRef.current.setMap(null);
        polyRef.current = null;
      }
    };
  }, [map, preview]);

  useEffect(() => {
    guideRefs.current.forEach((line) => line.setMap(null));
    guideRefs.current = [];
    if (
      !map ||
      guides.length === 0 ||
      typeof google === 'undefined' ||
      !google.maps ||
      !google.maps.Polyline
    ) {
      return;
    }

    const created = guides.map(
      (g) =>
        new google.maps.Polyline({
          path: [
            { lat: g.from[0], lng: g.from[1] },
            { lat: g.to[0], lng: g.to[1] },
          ],
          strokeColor: g.kind === 'orthogonal' ? '#a855f7' : '#38bdf8',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          clickable: false,
          zIndex: 65,
          map,
        }),
    );
    guideRefs.current = created;
    return () => {
      created.forEach((line) => line.setMap(null));
      guideRefs.current = [];
    };
  }, [map, guides]);

  if (!preview && guides.length === 0) return null;
  return (
    <div
      data-testid="boundary-alignment-preview-overlay"
      data-has-preview={Boolean(preview)}
      data-guide-count={guides.length}
      className="sr-only"
    />
  );
};
